import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { api } from '@/lib/api'

const TOKEN_KEY = 'lp_admin_token'

export type AdminRole = 'SUPER_ADMIN' | 'ADMIN' | 'READ_ONLY'

export interface AdminUser {
  id: number
  email: string
  name: string
  role: AdminRole
}

interface AuthState {
  user: AdminUser | null
  token: string | null
  loading: boolean
  login: (token: string, user: AdminUser) => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Hydrate from localStorage on boot
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY)
    if (!stored) {
      setLoading(false)
      return
    }
    api<{ ok: boolean; user: AdminUser }>('/me.php', { method: 'GET', token: stored })
      .then(res => {
        setUser(res.user)
        setToken(stored)
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY)
      })
      .finally(() => setLoading(false))
  }, [])

  const login = (newToken: string, newUser: AdminUser) => {
    localStorage.setItem(TOKEN_KEY, newToken)
    setToken(newToken)
    setUser(newUser)
  }

  const logout = async () => {
    if (token) {
      try {
        await api('/auth/logout.php', { method: 'POST', token })
      } catch {
        /* logout is best-effort */
      }
    }
    localStorage.removeItem(TOKEN_KEY)
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
